<script setup>
import { useClipboard } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PlatformShare from '@/components/posts/PlatformShare.vue';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { patchHeldPost, postModelForFeed } from '@/models/posts/postFeedModels.js';

const props = defineProps({
  postId: {
    type: [String, Number],
    required: true,
  },
  currentFeed: {
    type: [String],
    required: true,
  },
  currentContent: {
    type: String,
    default: '',
  },
  showBuyerActions: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['share-success']);

const { t } = useI18n();

// Read from the feed's own table, like PostCard. Writes go through
// patchHeldPost: every table that already holds this post, never an insert — a
// blind `Post` save from the تدبيسة page or the bookmarks tab would plant an
// empty ghost card in the home feed (see models/posts/postFeedModels.js).
const currentRepo = computed(() => useRepo(postModelForFeed(props.currentFeed)));

const post = computed(() => currentRepo.value.with('channel').with('products').with('media').find(props.postId));

const copied = ref(false);
const { copy } = useClipboard();

const liked = computed(() => post.value?.isLiked ?? false);
const bookmarked = computed(() => post.value?.isBookmarked ?? false);
const likes = computed(() => post.value?.likesCount ?? 0);
const bookmarks = computed(() => post.value?.bookmarksCount ?? 0);

const contentToCopy = computed(() => {
  return props.currentContent || post.value?.content || '';
});

const formatCount = (count) => {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};

const handleLike = async () => {
  if (!post.value) return;

  const originalState = {
    isLiked: post.value.isLiked,
    likesCount: post.value.likesCount,
  };

  try {
    patchHeldPost({
      id: props.postId,
      isLiked: !originalState.isLiked,
      likesCount: originalState.isLiked
        ? Math.max(0, originalState.likesCount - 1)
        : originalState.likesCount + 1,
    });

    const method = originalState.isLiked ? 'DELETE' : 'POST';
    await jsonApiClient.request({ url: `/posts/${props.postId}/like`, method });
  } catch {
    patchHeldPost({ id: props.postId, ...originalState });
    f7.toast.create({
      text: 'فشل تحديث الإعجاب',
      position: 'center',
      closeTimeout: 2000,
    }).open();
  }
};

const handleBookmark = async () => {
  if (!post.value) return;

  const originalState = {
    isBookmarked: post.value.isBookmarked,
    bookmarksCount: post.value.bookmarksCount,
  };

  try {
    patchHeldPost({
      id: props.postId,
      isBookmarked: !originalState.isBookmarked,
      bookmarksCount: originalState.isBookmarked
        ? Math.max(0, originalState.bookmarksCount - 1)
        : originalState.bookmarksCount + 1,
    });

    const method = originalState.isBookmarked ? 'delete' : 'post';
    await jsonApiClient.request({ url: `/posts/${props.postId}/bookmark`, method });

  } catch {
    patchHeldPost({ id: props.postId, ...originalState });
    f7.toast.create({
      text: 'فشل تحديث الإشارة المرجعية',
      position: 'center',
      closeTimeout: 2000,
    }).open();
  }
};

const handleCopy = async () => {
  if (!contentToCopy.value) return;

  try {
    await copy(contentToCopy.value);
    copied.value = true;

    f7.toast.create({
      text: 'تم النسخ بنجاح',
      position: 'center',
      closeTimeout: 2000,
    }).open();

    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    f7.toast.create({
      text: 'فشل النسخ',
      position: 'center',
      closeTimeout: 2000,
    }).open();
  }
};

const handleShareSuccess = (data) => {
  emit('share-success', data);
};
</script>

<template>
  <f7-card-footer class="actions-footer">
    <div class="actions-group">
      <!-- Sellers: interactive bookmark toggle -->
      <f7-button
        v-if="showBuyerActions"
        dir="ltr"
        large
        tonal
        round
        raised

        :text="bookmarked?'':'حفظ'"

        :class="{ active: bookmarked }"
        @click="handleBookmark"
      >
        <f7-icon
          :ios="bookmarked ? 'f7:bookmark_fill' : 'f7:bookmark'"
          :md="bookmarked ? 'material:bookmark' : 'material:bookmark_border'"
          size="20"
        >
          <f7-badge>{{ formatCount(bookmarks) }}</f7-badge>
        </f7-icon>


      </f7-button>

      <!--
        Suppliers (D2): the interactive toggle is hidden (a supplier's own saves
        have no destination screen), but the bookmark COUNT is the demand signal
        the feed surfaces and the popularity sort ranks by — so show it read-only.
      -->
      <div
        v-else
        class="bookmark-stat"
        dir="ltr"
        :aria-label="t('supplier.feed.card.savesAria', { count: bookmarks })"
      >
        <f7-icon
          class="bookmark-stat__icon"
          ios="f7:bookmark_fill"
          md="material:bookmark"
          size="20"
        ></f7-icon>
        <span class="bookmark-stat__count">{{ formatCount(bookmarks) }}</span>
      </div>
    </div>

    <div class="actions-group">
      <f7-button

        :class="{ active: liked }"
        @click="handleLike"
      >
        <f7-icon
          :ios="liked ? 'f7:heart_fill' : 'f7:heart'"
          :md="liked ? 'material:favorite' : 'material:favorite_border'"
        >
          <f7-badge color="red">{{ formatCount(likes) }}</f7-badge>
        </f7-icon>


      </f7-button>

      <f7-button
        round
        outline
        icon-only
        class="action-btn copy-btn"
        :class="{ active: copied }"
        @click="handleCopy"
      >
        <f7-icon
          :ios="copied ? 'f7:checkmark' : 'f7:doc_on_clipboard'"
          :md="copied ? 'material:check' : 'material:content_copy'"
          size="18"
        ></f7-icon>
      </f7-button>

      <platform-share
        v-if="post"
        :channel-name="post.channelName"
        :post-id="post.id"
        :content="contentToCopy"
        @share-success="handleShareSuccess"
      ></platform-share>
    </div>
  </f7-card-footer>
</template>

<style scoped>
.actions-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--poolit-spacing-md);
    padding: var(--poolit-spacing-md);
    border-top: 1px solid var(--f7-card-outline-border-color);
}

.actions-group {
    display: flex;
    align-items: center;
    gap: var(--poolit-spacing-sm);
}

.action-btn {
    --f7-button-height: 40px;
    --f7-button-padding-horizontal: var(--poolit-spacing-md);
    --f7-button-border-color: var(--poolit-border-color);
    --f7-button-text-color: var(--poolit-text-secondary);
    --f7-button-font-size: var(--poolit-font-size-small);
    --f7-button-font-weight: var(--poolit-font-weight-semibold);
    transition: all var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.action-btn:active {
    transform: scale(0.95);
}

.bookmark-btn {
    --f7-button-border-color: var(--poolit-warning-500);
    --f7-button-text-color: var(--poolit-warning-600);
}

.bookmark-btn.active {
    --f7-button-bg-color: var(--poolit-warning-500);
    --f7-button-text-color: white;
    --f7-button-border-color: var(--poolit-warning-600);
    box-shadow: 0 4px 12px rgba(var(--poolit-warning-rgb), 0.3);
}

.like-btn {
    --f7-button-border-color: var(--poolit-border-color);
    --f7-button-text-color: var(--poolit-text-tertiary);
}

.like-btn.active {
    --f7-button-bg-color: var(--poolit-error-50);
    --f7-button-text-color: var(--poolit-error-600);
    --f7-button-border-color: var(--poolit-error-200);
}

.copy-btn {
    --f7-button-width: 40px;
    --f7-button-padding-horizontal: 0;
    --f7-button-border-color: var(--poolit-border-color);
    --f7-button-text-color: var(--poolit-text-tertiary);
}

.copy-btn.active {
    --f7-button-bg-color: var(--poolit-success-50);
    --f7-button-text-color: var(--poolit-success-600);
    --f7-button-border-color: var(--poolit-success-200);
}

.btn-count {
    min-width: 24px;
    text-align: center;
    margin-left: 6px;
}

.dark .action-btn {
    --f7-button-border-color: var(--poolit-border-color-on-dark);
    --f7-button-text-color: var(--poolit-text-tertiary-dark);
}

.dark .bookmark-btn {
    --f7-button-border-color: var(--poolit-warning-600);
    --f7-button-text-color: var(--poolit-warning-500);
}

.dark .bookmark-btn.active {
    --f7-button-bg-color: var(--poolit-warning-600);
}

.dark .like-btn.active {
    --f7-button-bg-color: rgba(var(--poolit-error-rgb), 0.2);
    --f7-button-text-color: var(--poolit-error-400);
    --f7-button-border-color: var(--poolit-error-600);
}

.dark .copy-btn.active {
    --f7-button-bg-color: rgba(var(--poolit-success-rgb), 0.2);
    --f7-button-text-color: var(--poolit-success-500);
    --f7-button-border-color: var(--poolit-success-600);
}

/* Read-only popularity stat — supplier feed only (replaces the hidden bookmark toggle).
   Shows how many sellers saved the post: the demand signal + popularity-sort anchor. */
.bookmark-stat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    background: var(--poolit-warning-50);
    border: 1px solid rgba(var(--poolit-warning-rgb), 0.28);
    color: var(--poolit-warning-700);
    user-select: none;
}

.bookmark-stat__icon {
    color: var(--poolit-warning-600);
}

.bookmark-stat__count {
    font-size: 17px;
    font-weight: var(--poolit-font-weight-bold);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    letter-spacing: 0.01em;
}

.dark .bookmark-stat {
    background: rgba(var(--poolit-warning-rgb), 0.16);
    border-color: rgba(var(--poolit-warning-rgb), 0.4);
    color: var(--poolit-warning-500);
}

.dark .bookmark-stat__icon {
    color: var(--poolit-warning-500);
}

@supports (padding: max(0px)) {
    .actions-footer {
        padding-left: max(var(--poolit-spacing-md), env(safe-area-inset-left));
        padding-right: max(var(--poolit-spacing-md), env(safe-area-inset-right));
    }
}
</style>
